import { getUrlAppId } from './bylineAppDomains.ts';
import { getFirstPartyAppIconId } from './getProductAppIcon.ts';

/**
 * One app identity for every byline source, so the server can count distinct apps across Dev Docs,
 * Related content, Appears in and Links to before paint (CCINT-9543, DACI Option 3 + 4).
 *
 * The sources name the same app differently: the Confluence backend returns `product: "jira"` for
 * an outgoing link, the graph returns a `JiraIssue` node for an incoming one, Dev Docs returns a
 * `provider` string, and third-party graph rows carry a connector `provider` whose `name` is a
 * display label ("Google Calendar provider"). Every rule below normalises to one lowercase id:
 * the first-party app id the icon stack already draws (`confluence`, `jira`, `goals`, …), else the
 * normalised provider name, else the app behind the URL's host. `null` means "not an app we can
 * name", which never counts.
 */
export type BylineAppId = string;

// Owners that identify an Atlassian product in `ari:cloud:<owner>:…`. `graph` is a third-party
// container and says nothing about the app.
const ARI_OWNER_APP_IDS: Record<string, BylineAppId> = {
	bitbucket: 'bitbucket',
	compass: 'compass',
	confluence: 'confluence',
	jira: 'jira',
	loom: 'loom',
	mercury: 'goals',
	townsquare: 'goals',
	trello: 'trello',
};

// Graph node type prefixes for Atlassian products, matching the catalogue's `ati:cloud:<owner>`.
// Order matters only where one prefix starts another; none of these do.
const GRAPH_TYPENAME_PREFIX_APP_IDS: ReadonlyArray<readonly [string, BylineAppId]> = [
	['Bitbucket', 'bitbucket'],
	['Compass', 'compass'],
	['Confluence', 'confluence'],
	['Jira', 'jira'],
	['Loom', 'loom'],
	['Mercury', 'goals'],
	['Townsquare', 'goals'],
];

const ARI_OWNER_PATTERN = /^ari:cloud:([a-z0-9-]+):/;

// One app can run several connectors: Google Drive rows arrive from both "Google Drive provider" and
// "Google Drive Rich Smartlink provider", which must count as one app. CCINT-9674
const CONNECTOR_LABEL_SUFFIX = /\s+(?:rich\s+)?(?:smart\s*link\s+)?(?:provider|connector)$/;

/** Strips the connector label suffix and case: "Google Calendar provider" → "google calendar". */
const normalizeProviderName = (name: string): string =>
	name.trim().toLowerCase().replace(CONNECTOR_LABEL_SUFFIX, '').trim();

/** The Atlassian product that owns an ARI, or `null` for third-party and malformed ARIs. */
export const getAriOwnerAppId = (ari: string | null | undefined): BylineAppId | null => {
	const owner = ari ? ARI_OWNER_PATTERN.exec(ari)?.[1] : undefined;
	return owner ? (ARI_OWNER_APP_IDS[owner] ?? null) : null;
};

export type BylineProviderIdentity = {
	readonly name?: string | null;
	readonly providerId?: string | null;
};

/**
 * The app behind a third-party graph row's connector. Atlassian apps a connector can also surface
 * (Bitbucket) collapse onto the first-party id so Dev Docs, Links to and Appears in agree.
 */
export const getProviderAppId = (
	provider: BylineProviderIdentity | null | undefined,
): BylineAppId | null => {
	const name = provider?.name ? normalizeProviderName(provider.name) : '';
	if (name) {
		return getFirstPartyAppIconId(name) ?? name;
	}
	return provider?.providerId ? `provider:${provider.providerId}` : null;
};

export type GraphNodeIdentityInput = {
	/** The row's ARI, always present. */
	readonly ari: string;
	/** The hydrated node's `__typename`; `null` when AGG could not hydrate the row. */
	readonly typename?: string | null;
	/** The connector on `External*` types, when the query selected it. */
	readonly provider?: BylineProviderIdentity | null;
	/** The node's web URL, the last resort for a third-party row without a provider. */
	readonly url?: string | null;
};

/**
 * The app behind a hydrated Appears in or Related content row. First-party rows name it in the type
 * or the ARI; third-party rows need the connector `provider`, else the URL's host. Callers skip
 * unhydrated rows (`data: null`) before asking, since the client never renders them.
 */
export const getGraphNodeAppId = ({
	ari,
	typename,
	provider,
	url,
}: GraphNodeIdentityInput): BylineAppId | null => {
	if (typename) {
		const byPrefix = GRAPH_TYPENAME_PREFIX_APP_IDS.find(([prefix]) => typename.startsWith(prefix));
		if (byPrefix) {
			return byPrefix[1];
		}
	}
	return getAriOwnerAppId(ari) ?? getProviderAppId(provider) ?? getUrlAppId(url);
};

export type OutboundLinkIdentityInput = {
	readonly url: string;
	/** The Confluence backend's product, filled for first-party Smart Links only. */
	readonly product?: string | null;
};

/**
 * The app behind a Links to row. The backend resolves Atlassian products itself; everything else
 * (third-party Smart Links and plain hyperlinks) is identified by its host, or not at all.
 */
export const getOutboundLinkAppId = ({
	url,
	product,
}: OutboundLinkIdentityInput): BylineAppId | null =>
	getFirstPartyAppIconId(product) ?? getUrlAppId(url);

/** The app behind a Dev Docs synced folder: its source-control provider (`bitbucket`, `github`). */
export const getDevDocsAppId = (provider: string | null | undefined): BylineAppId | null => {
	const key = provider?.trim().toLowerCase();
	return key ? (getFirstPartyAppIconId(key) ?? key) : null;
};

/** Distinct app ids in first-seen order, skipping rows that identified nothing. */
export const dedupeAppIds = (appIds: Iterable<BylineAppId | null | undefined>): BylineAppId[] => {
	const seen = new Set<BylineAppId>();
	const result: BylineAppId[] = [];
	for (const appId of appIds) {
		if (appId && !seen.has(appId)) {
			seen.add(appId);
			result.push(appId);
		}
	}
	return result;
};
